import { yupResolver } from '@hookform/resolvers/yup';
import { unwrapResult } from '@reduxjs/toolkit';
import { CloseCircleIcon } from '@velocity/icons/system';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { cancelLeaseApplication } from '../../../../api/instant-lease-api';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import {
  fetchLeaseApp,
  setTradeRegistry,
  updateCustomerData,
} from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import {
  APIPaths,
  Company,
  Individual,
  LeaseApplication,
  LegalForm,
} from '../../../../types/instant-lease-api';
import {
  getBundeslands,
  getCommercialRegisterHeadlineText,
  getText,
} from '../../../../utils/getter';
import {
  hasCommercialRegisterValidation,
  hideCommercialRegisterSection,
} from '../../../../utils/helper';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import FormLayout from '../../../common/form-layout/form-layout';
import InputField from '../../../common/input-field/input-field';
import SelectBox from '../../../common/select-box/select-box';
import ArrowList from '../../components/arrow-list/arrow-list';
import Modal from '../../components/modal/modal';
import ButtonGroup from '../../molecules/button-group/button-group';

interface CommercialRegisterProps {
  getInitialValue: (leaseApp: LeaseApplication | null) => Company | Individual;
}

const CmTrRegistry: React.FC<CommercialRegisterProps> = ({
  getInitialValue,
}) => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Loading State
  const [loading, setLoading] = useState<boolean>();

  // dispatch hook
  const dispatch = useAppDispatch();

  // Histroy Hook
  const navigate = useNavigate();

  // Navigation
  const { nextRoute } = new Navigation();

  // modal visibility
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);

  // Validation Schema
  const validationSchema = yup.object().shape({
    trade_registry_number: yup.string().when('any', {
      is: hasCommercialRegisterValidation(activeApplication),
      then: (schema) => schema.required(getText('validation_number')),
      otherwise: (schema) => schema.notRequired(),
    }),
    local_court: yup.string().when('any', {
      is: hasCommercialRegisterValidation(activeApplication),
      then: (schema) => schema.min(2, 'Pflichtfeld').required('Pflichtfeld'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // React form hook
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  });

  // Get initial values
  useEffect(() => {
    const company = getInitialValue(activeApplication);
    if (company && (company.trade_registry_number || company.local_court)) {
      setValue('trade_registry_number', company?.trade_registry_number || '');
      setValue('local_court', company?.local_court || '');
      trigger();
    }
  }, [getInitialValue, activeApplication, setValue, trigger]);

  // Get text based on legal entity of the company
  const getTitle = (): string => {
    return getCommercialRegisterHeadlineText(activeApplication).headline;
  };

  // Cancel leasing application
  const cancelCurrentLeaseApp = () => {
    setLoading(true);
    cancelLeaseApplication(accessToken, activeApplication?.uuid as string)
      .then(() => {
        dispatch(
          fetchLeaseApp({
            accessToken,
            leaseAppId: activeApplication?.uuid || '',
          }),
        );
        setLoading(false);
        setModalVisibility(false);
        navigate('/');
      })
      .catch(() => {
        setLoading(false);
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: true,
            notificationTitle: 'Das tut uns leid',
            notificationBody: getText('error_unable_to_process_data'),
          }),
        );
      });
  };

  // Disable weiter button
  const disableButton = (): boolean => false;

  // Submit values
  const submit = ({
    trade_registry_number,
    local_court,
  }: {
    trade_registry_number?: string | undefined;
    local_court?: string | undefined;
  }) => {
    setLoading(true);
    if (trade_registry_number && local_court) {
      dispatch(
        updateCustomerData({
          accessToken,
          leaseApplicationId: activeApplication?.uuid || '',
          inputValue: {
            trade_registry_number,
            local_court,
          },
          path: APIPaths.TRADE_REGISTRY,
        }),
      )
        .then(unwrapResult)
        .then(() => {
          setLoading(false);
          dispatch(setTradeRegistry(trade_registry_number));
          navigate(nextRoute());
        })
        .catch(() => {
          setLoading(false);
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
          errorLogging(
            new Error(
              `unable to submit trade registers data to user with application id ${activeApplication?.uuid}`,
            ),
          );
        });
    } else {
      setLoading(false);
      navigate(nextRoute());
    }
  };

  return (
    <Layout heading={getTitle()} stepStatus>
      {!hideCommercialRegisterSection(activeApplication) && (
        <FormLayout
          sectionTitle={
            getCommercialRegisterHeadlineText(activeApplication).headline
          }
        >
          <form onSubmit={handleSubmit(submit)}>
            <div>
              <InputField
                label={
                  getCommercialRegisterHeadlineText(activeApplication)
                    .tradeRegistrytext
                }
                extraLabel={
                  !hasCommercialRegisterValidation(activeApplication)
                    ? ' (optional)'
                    : ''
                }
                {...register('trade_registry_number')}
                error={errors.trade_registry_number?.message}
              />
              {activeApplication?.customer?.company?.legal_form ===
              LegalForm.STIFTUNG ? (
                <SelectBox
                  initialValue={watch('local_court')}
                  setValueCallBack={(value) => {
                    setValue('local_court', value, { shouldValidate: true });
                    trigger();
                  }}
                  label={
                    getCommercialRegisterHeadlineText(activeApplication)
                      .localCourtText
                  }
                  placeholder={
                    getCommercialRegisterHeadlineText(activeApplication)
                      .localCourtText
                  }
                  name='local_court'
                  options={getBundeslands}
                  hasError={!!errors.local_court?.message}
                  errorMessage={errors.local_court?.message}
                />
              ) : (
                <InputField
                  label={
                    getCommercialRegisterHeadlineText(activeApplication)
                      .localCourtText
                  }
                  extraLabel={
                    !hasCommercialRegisterValidation(activeApplication)
                      ? ' (optional)'
                      : ''
                  }
                  {...register('local_court')}
                  error={errors.local_court?.message}
                />
              )}

              {hasCommercialRegisterValidation(activeApplication) && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '10px',
                    color: '#5386e4',
                  }}
                >
                  <span
                    className='button-text top-16'
                    role='presentation'
                    onClick={() => {
                      setModalVisibility(true);
                    }}
                  >
                    Nicht im Register eingetragen
                  </span>
                </div>
              )}
            </div>
          </form>
        </FormLayout>
      )}

      <ButtonGroup
        className='back-next'
        type='back-next'
        data-testid='disabled-forward-button'
        buttonOneProps={{
          type: 'button',
          onClick: handleSubmit(submit),
          name: 'form-submit',
          loading,
          disabled: disableButton(),
          dataTestId: 'next',
          children: 'nächster Schritt',
        }}
      />
      <Modal
        isOpen={modalVisibility}
        onClose={() => setModalVisibility(false)}
        closeButtonVisibility={false}
        type='middle'
        direction='down'
      >
        <h2 className='heading left'>
          Deine Firma hat keinen Registereintrag?
        </h2>
        <ArrowList
          className='dark-grey content-normal'
          list={[getText('no_register')]}
        />

        <ButtonGroup
          type='default'
          className='top-48'
          buttonOneProps={{
            type: 'button',
            variant: 'secondary-outlined',
            loading,
            onClick: () => setModalVisibility(false),
            children: 'Fortsetzen',
          }}
          buttonTwoProps={{
            type: 'button',
            StartIcon: CloseCircleIcon,
            loading,
            onClick: () => cancelCurrentLeaseApp(),
            children: 'Leasingantrag stornieren',
          }}
        />
      </Modal>
    </Layout>
  );
};

export default CmTrRegistry;
